import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { normalizeVerticalValue, type Vertical } from '../../../core/enums/verticals';

type JsonRecord = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class CompanyVerticalApiService {
  private readonly http = inject(HttpClient);

  /**
   * Lê a vertical da Company corrente (resolvida pelo header X-Company-Id).
   *
   * Retorno:
   * - `Vertical`: campo presente e valor válido.
   * - `null`: campo presente no DTO mas explicitamente nulo (cliente pré-A0).
   * - `undefined`: campo AUSENTE do DTO (backend antigo) ou erro de fetch — defensivo;
   *   o consumidor deve tratar como "não-conhecido" e NÃO disparar o modal.
   */
  async getCurrentVertical(activeCompanyId: string | null): Promise<Vertical | null | undefined> {
    const raw = await this.tryGet([
      '/api/companies',
      '/api/companies/current',
    ]);
    if (raw === null) return undefined;

    const record = this.resolveCompanyRecord(raw, activeCompanyId);
    if (!record) return undefined;

    return this.normalizeVerticalField(record);
  }

  async updateVertical(vertical: Vertical, activeCompanyId: string | null): Promise<void> {
    const body: JsonRecord = { vertical };
    const endpoints = ['/api/companies/vertical'];
    if (activeCompanyId?.trim()) {
      endpoints.push(`/api/companies/${encodeURIComponent(activeCompanyId.trim())}/vertical`);
    }

    const ok = await this.tryPut(endpoints, body);
    if (!ok) {
      throw new Error('Não foi possível atualizar a vertical da empresa.');
    }
  }

  private async tryGet(endpoints: string[]): Promise<unknown | null> {
    for (const endpoint of endpoints) {
      try {
        return await firstValueFrom(this.http.get<unknown>(endpoint));
      } catch {
        // tenta o próximo
      }
    }
    return null;
  }

  private async tryPut(endpoints: string[], body: unknown): Promise<boolean> {
    for (const endpoint of endpoints) {
      try {
        await firstValueFrom(this.http.put(endpoint, body));
        return true;
      } catch (error) {
        // 400/402/403/409: endpoint existe e respondeu — propaga (payload inválido / sem permissão / conflito).
        if (
          error instanceof HttpErrorResponse &&
          (error.status === 400 ||
            error.status === 402 ||
            error.status === 403 ||
            error.status === 409)
        ) {
          throw error;
        }
        // 404 / 500 / network: tenta o próximo endpoint.
      }
    }
    return false;
  }

  /** Aceita objeto único, lista (filtra por id) ou wrappers comuns (`data`, `company`). */
  private resolveCompanyRecord(raw: unknown, activeCompanyId: string | null): JsonRecord | null {
    if (Array.isArray(raw)) {
      return this.findByActiveId(raw, activeCompanyId);
    }
    const root = this.asRecord(raw);
    if (!root) return null;

    const wrapperArray =
      this.asArray(root['items']) ??
      this.asArray(root['Items']) ??
      this.asArray(root['data']) ??
      this.asArray(root['Data']) ??
      this.asArray(root['companies']) ??
      this.asArray(root['Companies']) ??
      this.asArray(root['results']) ??
      this.asArray(root['Results']);
    if (wrapperArray) {
      return this.findByActiveId(wrapperArray, activeCompanyId);
    }

    const nested =
      this.asRecord(root['company']) ??
      this.asRecord(root['Company']) ??
      this.asRecord(root['data']) ??
      this.asRecord(root['Data']);
    if (nested && this.looksLikeCompanyRecord(nested)) return nested;

    return root;
  }

  private findByActiveId(items: unknown[], activeCompanyId: string | null): JsonRecord | null {
    if (!items.length) return null;
    if (activeCompanyId?.trim()) {
      const target = activeCompanyId.trim().toLowerCase();
      for (const item of items) {
        const record = this.asRecord(item);
        if (!record) continue;
        const id = this.readIdString(record);
        if (id && id.toLowerCase() === target) return record;
      }
    }
    const first = this.asRecord(items[0]);
    return first;
  }

  private readIdString(record: JsonRecord): string {
    const direct = record['id'] ?? record['Id'] ?? record['companyId'] ?? record['CompanyId'];
    return typeof direct === 'string' ? direct.trim() : '';
  }

  private looksLikeCompanyRecord(record: JsonRecord): boolean {
    const keys = ['id', 'Id', 'name', 'Name', 'tradeName', 'TradeName', 'companyKey', 'CompanyKey'];
    return keys.some((key) => key in record);
  }

  /**
   * Distingue 3 estados:
   * - chave AUSENTE → undefined (DTO sem campo, cliente legacy)
   * - valor `null` → null (explícito, empresa sem vertical escolhida)
   * - número do enum (0..6) ou string conhecida → Vertical
   * - valor desconhecido → null (degrada com segurança)
   */
  private normalizeVerticalField(record: JsonRecord): Vertical | null | undefined {
    const hasKey = 'vertical' in record || 'Vertical' in record;
    if (!hasKey) return undefined;
    const raw = record['vertical'] ?? record['Vertical'];
    return normalizeVerticalValue(raw);
  }

  private asRecord(value: unknown): JsonRecord | null {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : null;
  }

  private asArray(value: unknown): unknown[] | null {
    return Array.isArray(value) ? value : null;
  }
}
